import React, { useState } from 'react';
import {
  Plus,
  Trash2,
  Users,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Linkedin,
  Mail,
  Twitter,
  Phone,
  Copy,
  LayoutGrid,
} from 'lucide-react';
import { PageSection } from '../../../../types';

interface TeamEditorProps {
  section: PageSection;
  onUpdateContent: (updates: any) => void;
}

export const TeamEditor: React.FC<TeamEditorProps> = ({ section, onUpdateContent }) => {
  const content = section.content || {};
  const columns = content.columns || 3;

  const items: any[] = content.items || [
    {
      id: '1',
      name: 'Dr. Selim Aras',
      role: 'Yönetim Kurulu Başkanı',
      bio: '25 yıllık uluslararası yönetim ve finans deneyimi.',
      avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=400&auto=format&fit=crop',
      socials: {
        linkedin: 'https://linkedin.com',
        email: 'selim@sirket.com',
      },
    },
  ];

  const [activeIdx, setActiveIdx] = useState<number | null>(0);

  const handleUpdateMember = (index: number, updates: any) => {
    const next = [...items];
    const current = next[index] || {};

    // Keep both name/title and role/subtitle in sync
    const merged = { ...current, ...updates };
    if (updates.name !== undefined) merged.title = updates.name;
    if (updates.role !== undefined) merged.subtitle = updates.role;
    if (updates.bio !== undefined) merged.description = updates.bio;
    if (updates.avatar !== undefined) merged.imageUrl = updates.avatar;

    next[index] = merged;
    onUpdateContent({ items: next });
  };

  const handleUpdateSocial = (index: number, network: 'linkedin' | 'email' | 'twitter' | 'phone', val: string) => {
    const next = [...items];
    const current = next[index] || {};
    const socials = { ...(current.socials || {}), [network]: val };
    next[index] = { ...current, socials };
    onUpdateContent({ items: next });
  };

  const handleAddMember = () => {
    const newMember = {
      id: `team_${Date.now()}`,
      name: 'Yeni Ekip Üyesi',
      title: 'Yeni Ekip Üyesi',
      role: 'Uzman / Danışman',
      subtitle: 'Uzman / Danışman',
      bio: 'Deneyim ve uzmanlık alanları hakkında kısa özet.',
      description: 'Deneyim ve uzmanlık alanları hakkında kısa özet.',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=400&auto=format&fit=crop',
      imageUrl: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=400&auto=format&fit=crop',
      socials: {
        linkedin: '',
        email: '',
      },
    };
    const next = [...items, newMember];
    onUpdateContent({ items: next });
    setActiveIdx(next.length - 1);
  };

  const handleDuplicateMember = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const source = items[index];
    const duplicate = {
      ...source,
      id: `team_${Date.now()}`,
      name: `${source.name || source.title || 'Ekip Üyesi'} (Kopya)`,
      title: `${source.name || source.title || 'Ekip Üyesi'} (Kopya)`,
    };
    const next = [...items.slice(0, index + 1), duplicate, ...items.slice(index + 1)];
    onUpdateContent({ items: next });
    setActiveIdx(index + 1);
  };

  const handleDeleteMember = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const next = items.filter((_, i) => i !== index);
    onUpdateContent({ items: next });
    if (activeIdx === index) setActiveIdx(null);
  };

  const handleMove = (index: number, direction: 'up' | 'down', e: React.MouseEvent) => {
    e.stopPropagation();
    const targetIdx = direction === 'up' ? index - 1 : index + 1;
    if (targetIdx < 0 || targetIdx >= items.length) return;
    const next = [...items];
    const temp = next[index];
    next[index] = next[targetIdx];
    next[targetIdx] = temp;
    onUpdateContent({ items: next });
    setActiveIdx(targetIdx);
  };

  return (
    <div className="space-y-6">
      {/* Section Headings & Layout */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-blue-400" />
            <h4 className="text-xs font-bold text-slate-200">Bölüm Başlıkları & Düzen</h4>
          </div>
          <div className="flex items-center gap-1">
            {[2, 3, 4].map((col) => (
              <button
                key={col}
                type="button"
                onClick={() => onUpdateContent({ columns: col })}
                className={`px-2 py-0.5 rounded-lg text-[10px] font-bold transition ${
                  columns === col
                    ? 'bg-blue-600 text-white shadow-xs'
                    : 'bg-slate-800 text-slate-400 hover:text-white'
                }`}
                title={`${col} Sütunlu Görünüm`}
              >
                {col} Sütun
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Üst Rozet</label>
          <input
            type="text"
            value={content.badge ?? 'LİDERLİK KADROSU'}
            onChange={(e) => onUpdateContent({ badge: e.target.value })}
            className="w-full bg-slate-900 border border-slate-700 text-xs font-semibold text-slate-200 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Ana Başlık</label>
          <input
            type="text"
            value={content.title ?? 'Deneyimli ve Vizyoner Ekibimiz'}
            onChange={(e) => onUpdateContent({ title: e.target.value })}
            className="w-full bg-slate-900 border border-slate-700 text-xs font-bold text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Açıklama</label>
          <textarea
            rows={2}
            value={content.description ?? ''}
            onChange={(e) => onUpdateContent({ description: e.target.value })}
            placeholder="Ekibinizi tanıtan kısa bir paragraf..."
            className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-200 px-3 py-2 rounded-xl outline-none focus:border-blue-500 resize-none"
          />
        </div>
      </div>

      {/* Members list */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-blue-400" />
            <h4 className="text-xs font-extrabold text-slate-200">Ekip Üyeleri ({items.length})</h4>
          </div>
          <button
            type="button"
            onClick={handleAddMember}
            className="px-2.5 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-[11px] font-bold flex items-center gap-1.5 shadow-sm transition active:scale-95"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Kişi Ekle</span>
          </button>
        </div>

        <div className="space-y-2.5">
          {items.map((m, idx) => {
            const isExpanded = activeIdx === idx;
            const name = m.name || m.title || 'Ekip Üyesi';
            const role = m.role || m.subtitle || 'Uzman';
            const bio = m.bio || m.description || '';
            const avatar = m.avatar || m.imageUrl || '';
            const socials = m.socials || {};

            return (
              <div
                key={m.id || idx}
                className={`rounded-2xl border transition-all ${
                  isExpanded
                    ? 'bg-slate-800/95 border-blue-500/50 shadow-md ring-1 ring-blue-500/20'
                    : 'bg-slate-800/40 border-slate-700/60 hover:border-slate-600'
                }`}
              >
                {/* Header row */}
                <div
                  onClick={() => setActiveIdx(isExpanded ? null : idx)}
                  className="p-3 flex items-center justify-between cursor-pointer select-none"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <img
                      src={avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=100&auto=format&fit=crop'}
                      alt={name}
                      className="w-9 h-9 rounded-full object-cover bg-slate-900 border border-slate-700 shrink-0"
                    />
                    <div className="min-w-0">
                      <div className="text-xs font-bold text-slate-200 truncate">{name}</div>
                      <div className="text-[10px] text-blue-400 font-semibold truncate">{role}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 text-slate-400">
                    <button
                      type="button"
                      disabled={idx === 0}
                      onClick={(e) => handleMove(idx, 'up', e)}
                      title="Yukarı Taşı"
                      className="p-1 hover:text-white disabled:opacity-20 transition"
                    >
                      <ChevronUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      disabled={idx === items.length - 1}
                      onClick={(e) => handleMove(idx, 'down', e)}
                      title="Aşağı Taşı"
                      className="p-1 hover:text-white disabled:opacity-20 transition"
                    >
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleDuplicateMember(idx, e)}
                      title="Çoğalt"
                      className="p-1 hover:text-blue-400 transition"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleDeleteMember(idx, e)}
                      title="Sil"
                      className="p-1 hover:text-rose-400 transition"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="p-4 pt-3 border-t border-slate-700/50 space-y-3.5 animate-in fade-in duration-150">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                          Adı Soyadı
                        </label>
                        <input
                          type="text"
                          value={name}
                          onChange={(e) => handleUpdateMember(idx, { name: e.target.value })}
                          placeholder="Dr. Ahmet Yılmaz"
                          className="w-full bg-slate-900 border border-slate-700 text-xs font-bold text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                          Görevi / Ünvanı
                        </label>
                        <input
                          type="text"
                          value={role}
                          onChange={(e) => handleUpdateMember(idx, { role: e.target.value })}
                          placeholder="Yönetici Direktör"
                          className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-200 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                        Fotoğraf URL'si
                      </label>
                      <input
                        type="url"
                        value={avatar}
                        onChange={(e) => handleUpdateMember(idx, { avatar: e.target.value })}
                        placeholder="https://images.unsplash.com/..."
                        className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-200 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                        Özgeçmiş / Biyografi
                      </label>
                      <textarea
                        rows={2}
                        value={bio}
                        onChange={(e) => handleUpdateMember(idx, { bio: e.target.value })}
                        placeholder="Kişinin deneyimi, uzmanlığı ve başarıları..."
                        className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-200 px-3 py-2 rounded-xl outline-none focus:border-blue-500 resize-none"
                      />
                    </div>

                    {/* Social Media Links */}
                    <div className="p-3 rounded-xl bg-slate-900/60 border border-slate-700/50 space-y-2.5">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                        Sosyal Medya & İletişim Bağlantıları
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div className="flex items-center gap-2 bg-slate-900 px-2.5 py-1.5 rounded-lg border border-slate-700">
                          <Linkedin className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                          <input
                            type="text"
                            value={socials.linkedin || ''}
                            onChange={(e) => handleUpdateSocial(idx, 'linkedin', e.target.value)}
                            placeholder="LinkedIn URL"
                            className="bg-transparent text-xs text-slate-200 outline-none w-full"
                          />
                        </div>
                        <div className="flex items-center gap-2 bg-slate-900 px-2.5 py-1.5 rounded-lg border border-slate-700">
                          <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <input
                            type="email"
                            value={socials.email || ''}
                            onChange={(e) => handleUpdateSocial(idx, 'email', e.target.value)}
                            placeholder="E-posta"
                            className="bg-transparent text-xs text-slate-200 outline-none w-full"
                          />
                        </div>
                        <div className="flex items-center gap-2 bg-slate-900 px-2.5 py-1.5 rounded-lg border border-slate-700">
                          <Twitter className="w-3.5 h-3.5 text-sky-400 shrink-0" />
                          <input
                            type="text"
                            value={socials.twitter || ''}
                            onChange={(e) => handleUpdateSocial(idx, 'twitter', e.target.value)}
                            placeholder="Twitter / X URL"
                            className="bg-transparent text-xs text-slate-200 outline-none w-full"
                          />
                        </div>
                        <div className="flex items-center gap-2 bg-slate-900 px-2.5 py-1.5 rounded-lg border border-slate-700">
                          <Phone className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                          <input
                            type="tel"
                            value={socials.phone || ''}
                            onChange={(e) => handleUpdateSocial(idx, 'phone', e.target.value)}
                            placeholder="Telefon"
                            className="bg-transparent text-xs text-slate-200 outline-none w-full"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
